import { supabase } from '@/lib/supabase'

export type FinanceMonthlySummary = {
  month: string
  revenue_gross_cents: number
  revenue_adjustments_cents: number
  revenue_net_cents: number
  fixed_costs_cents: number
  variable_costs_cents: number
  affiliate_payouts_paid_cents: number
  affiliate_payouts_fee_cents: number
  net_result_cents: number
  commissions_pending_cents: number
  commissions_earned_cents: number
  active_paying_accounts_count: number
  canceling_accounts_count: number
  past_due_accounts_count: number
}

export type FinanceMonthlyRevenueEstimate = {
  month: string
  estimated_revenue_cents: number
  already_paid_cents: number
  due_cents: number
  eligible_accounts_count: number
  paid_accounts_count: number
  due_accounts_count: number
  not_due_accounts_count: number
}

export type FinanceMonthlyRevenueByPlanRow = {
  plan_id: string | null
  plan_name: string
  plan_interval: string | null
  revenue_gross_cents: number
  revenue_adjustments_cents: number
  revenue_net_cents: number
  paid_invoices_count: number
  paying_accounts_count: number
}

export type FinanceMonthlyRevenueEstimateByPlanRow = {
  plan_id: string | null
  plan_name: string
  plan_interval: string | null
  estimated_revenue_cents: number
  already_paid_cents: number
  due_cents: number
  eligible_accounts_count: number
  paid_accounts_count: number
  due_accounts_count: number
  not_due_accounts_count: number
}

export type FinanceMonthlyTimeseriesRow = {
  month: string
  revenue_net_cents: number
  fixed_costs_cents: number
  variable_costs_cents: number
  affiliate_payouts_paid_cents: number
  affiliate_payouts_fee_cents: number
  net_result_cents: number
  active_paying_accounts_count: number
}

export type FinancePaymentItem = {
  invoice_id: string
  account_id: string
  account_name: string
  owner_email: string
  stripe_subscription_id: string | null
  status: string | null
  amount_paid_cents: number
  adjustments_total_cents: number
  net_total_cents: number
  currency: string
  paid_at: string | null
  hosted_invoice_url: string | null
  invoice_pdf: string | null
  line_description: string | null
  plan_id: string | null
  plan_name: string | null
  plan_interval: string | null
}

export type FinancePaymentsResult = {
  items: FinancePaymentItem[]
  total: number
}

export type FinanceCustomerRiskRow = {
  account_id: string
  account_name: string | null
  owner_email: string | null
  status: string | null
  cancel_at_period_end: boolean | null
  current_period_end: string | null
  plan_id: string | null
  plan_name: string | null
  plan_interval: string | null
  last_payment_at: string | null
  last_payment_amount_cents: number | null
  currency: string | null
  risk_reason: string | null
}

export async function getFinanceMonthlySummary(monthStartDate: string): Promise<FinanceMonthlySummary> {
  const { data, error } = await supabase.rpc('finance_monthly_summary_v1', { p_month: monthStartDate } as any)
  if (error) throw error
  const payload = data as any
  return {
    month: String(payload?.month || monthStartDate),
    revenue_gross_cents: Number(payload?.revenue_gross_cents || 0),
    revenue_adjustments_cents: Number(payload?.revenue_adjustments_cents || 0),
    revenue_net_cents: Number(payload?.revenue_net_cents || 0),
    fixed_costs_cents: Number(payload?.fixed_costs_cents || 0),
    variable_costs_cents: Number(payload?.variable_costs_cents || 0),
    affiliate_payouts_paid_cents: Number(payload?.affiliate_payouts_paid_cents || 0),
    affiliate_payouts_fee_cents: Number(payload?.affiliate_payouts_fee_cents || 0),
    net_result_cents: Number(payload?.net_result_cents || 0),
    commissions_pending_cents: Number(payload?.commissions_pending_cents || 0),
    commissions_earned_cents: Number(payload?.commissions_earned_cents || 0),
    active_paying_accounts_count: Number(payload?.active_paying_accounts_count || 0),
    canceling_accounts_count: Number(payload?.canceling_accounts_count || 0),
    past_due_accounts_count: Number(payload?.past_due_accounts_count || 0),
  }
}

export async function getFinanceMonthlyRevenueEstimate(monthStartDate: string): Promise<FinanceMonthlyRevenueEstimate> {
  const { data, error } = await supabase.rpc('finance_monthly_revenue_estimate_v1', { p_month: monthStartDate } as any)
  if (error) throw error
  const payload = data as any
  return {
    month: String(payload?.month || monthStartDate),
    estimated_revenue_cents: Number(payload?.estimated_revenue_cents || 0),
    already_paid_cents: Number(payload?.already_paid_cents || 0),
    due_cents: Number(payload?.due_cents || 0),
    eligible_accounts_count: Number(payload?.eligible_accounts_count || 0),
    paid_accounts_count: Number(payload?.paid_accounts_count || 0),
    due_accounts_count: Number(payload?.due_accounts_count || 0),
    not_due_accounts_count: Number(payload?.not_due_accounts_count || 0),
  }
}

export async function listFinanceMonthlyRevenueByPlan(monthStartDate: string): Promise<FinanceMonthlyRevenueByPlanRow[]> {
  const { data, error } = await supabase.rpc('finance_monthly_revenue_by_plan_v1', { p_month: monthStartDate } as any)
  if (error) throw error
  const rows = Array.isArray(data) ? (data as any[]) : []
  return rows.map((r) => ({
    plan_id: r?.plan_id ? String(r.plan_id) : null,
    plan_name: String(r?.plan_name || ''),
    plan_interval: r?.plan_interval ? String(r.plan_interval) : null,
    revenue_gross_cents: Number(r?.revenue_gross_cents || 0),
    revenue_adjustments_cents: Number(r?.revenue_adjustments_cents || 0),
    revenue_net_cents: Number(r?.revenue_net_cents || 0),
    paid_invoices_count: Number(r?.paid_invoices_count || 0),
    paying_accounts_count: Number(r?.paying_accounts_count || 0),
  }))
}

export async function listFinanceMonthlyRevenueEstimateByPlan(
  monthStartDate: string,
): Promise<FinanceMonthlyRevenueEstimateByPlanRow[]> {
  const { data, error } = await supabase.rpc('finance_monthly_revenue_estimate_by_plan_v1', { p_month: monthStartDate } as any)
  if (error) throw error
  const rows = Array.isArray(data) ? (data as any[]) : []
  return rows.map((r) => ({
    plan_id: r?.plan_id ? String(r.plan_id) : null,
    plan_name: String(r?.plan_name || ''),
    plan_interval: r?.plan_interval ? String(r.plan_interval) : null,
    estimated_revenue_cents: Number(r?.estimated_revenue_cents || 0),
    already_paid_cents: Number(r?.already_paid_cents || 0),
    due_cents: Number(r?.due_cents || 0),
    eligible_accounts_count: Number(r?.eligible_accounts_count || 0),
    paid_accounts_count: Number(r?.paid_accounts_count || 0),
    due_accounts_count: Number(r?.due_accounts_count || 0),
    not_due_accounts_count: Number(r?.not_due_accounts_count || 0),
  }))
}

export async function listFinanceMonthlyTimeseries(
  endMonthStartDate: string,
  months = 12,
): Promise<FinanceMonthlyTimeseriesRow[]> {
  const { data, error } = await supabase.rpc('finance_monthly_timeseries_v1', { p_end_month: endMonthStartDate, p_months: months } as any)
  if (error) throw error
  const rows = Array.isArray(data) ? (data as any[]) : []
  return rows.map((r) => ({
    month: String(r?.month || ''),
    revenue_net_cents: Number(r?.revenue_net_cents || 0),
    fixed_costs_cents: Number(r?.fixed_costs_cents || 0),
    variable_costs_cents: Number(r?.variable_costs_cents || 0),
    affiliate_payouts_paid_cents: Number(r?.affiliate_payouts_paid_cents || 0),
    affiliate_payouts_fee_cents: Number(r?.affiliate_payouts_fee_cents || 0),
    net_result_cents: Number(r?.net_result_cents || 0),
    active_paying_accounts_count: Number(r?.active_paying_accounts_count || 0),
  }))
}

export async function listFinanceMonthlyPayments(params: {
  monthStartDate: string
  search?: string
  page?: number
  pageSize?: number
}): Promise<FinancePaymentsResult> {
  const { monthStartDate, search = '', page = 1, pageSize = 50 } = params
  const { data, error } = await supabase.rpc('finance_monthly_payments_v1', {
    p_month: monthStartDate,
    p_search: search,
    p_page: page,
    p_page_size: pageSize,
  } as any)
  if (error) throw error
  const payload = (data || {}) as any
  const items = Array.isArray(payload?.items) ? payload.items : []
  return {
    items: items.map((it: any) => ({
      invoice_id: String(it?.invoice_id || ''),
      account_id: String(it?.account_id || ''),
      account_name: String(it?.account_name || ''),
      owner_email: String(it?.owner_email || ''),
      stripe_subscription_id: it?.stripe_subscription_id ? String(it.stripe_subscription_id) : null,
      status: it?.status ? String(it.status) : null,
      amount_paid_cents: Number(it?.amount_paid_cents || 0),
      adjustments_total_cents: Number(it?.adjustments_total_cents || 0),
      net_total_cents: Number(it?.net_total_cents || 0),
      currency: String(it?.currency || 'BRL'),
      paid_at: it?.paid_at ? String(it.paid_at) : null,
      hosted_invoice_url: it?.hosted_invoice_url ? String(it.hosted_invoice_url) : null,
      invoice_pdf: it?.invoice_pdf ? String(it.invoice_pdf) : null,
      line_description: it?.line_description ? String(it.line_description) : null,
      plan_id: it?.plan_id ? String(it.plan_id) : null,
      plan_name: it?.plan_name ? String(it.plan_name) : null,
      plan_interval: it?.plan_interval ? String(it.plan_interval) : null,
    })),
    total: Number(payload?.total || items.length),
  }
}

export async function listFinanceCustomersRisk(days = 15): Promise<FinanceCustomerRiskRow[]> {
  const { data, error } = await supabase.rpc('finance_customers_risk_v1', { p_days: days } as any)
  if (error) throw error
  const rows = Array.isArray(data) ? (data as any[]) : []
  return rows.map((r) => ({
    account_id: String(r?.account_id || ''),
    account_name: r?.account_name ?? null,
    owner_email: r?.owner_email ?? null,
    status: r?.status ?? null,
    cancel_at_period_end: typeof r?.cancel_at_period_end === 'boolean' ? r.cancel_at_period_end : null,
    current_period_end: r?.current_period_end ?? null,
    plan_id: r?.plan_id ?? null,
    plan_name: r?.plan_name ?? null,
    plan_interval: r?.plan_interval ?? null,
    last_payment_at: r?.last_payment_at ?? null,
    last_payment_amount_cents: (typeof r?.last_payment_amount_cents === 'number' ? r.last_payment_amount_cents : null),
    currency: r?.currency ?? null,
    risk_reason: r?.risk_reason ?? null,
  }))
}
